import { useCallback } from 'react';

import { useApolloClient } from '@apollo/client/react';
import { Stack } from '@mui/material';
import { useParams } from 'react-router-dom';

import {
  type ReportComponentProps,
  useReportContext,
} from '@modules/_shared/components/report-editor/ReportEditorContext';
import { ListingPropertyImages } from '@modules/listings/ListingPropertyImages/ListingPropertyImages';
import type { ListingBrochureType } from '@modules/reports/brochure-report/BrochureReportEditor/BrochureReportEditor';
import { STEP_PHOTOS_BROCHURE_FRAGMENT } from '@modules/reports/brochure-report/brochureReportsQueries';
import { FooterActionsComponentWithoutForm } from '@modules/reports/shared/FooterActionsComponentWithoutForm';
import { useUpdateListingBrochure } from '@modules/reports/shared/utils';

const StepPhotosBrochure = (props: ReportComponentProps) => {
  const { data: report } = useReportContext();
  const reportData = report as ListingBrochureType;

  const client = useApolloClient();
  const params = useParams();

  const [updateListingBrochure, updating] = useUpdateListingBrochure(
    report.id,
    'page-photos',
  );

  const handleShowCaptionsChange = useCallback(
    (show: boolean) => {
      updateListingBrochure({
        show_image_captions_on_brochure: show,
      });
    },
    [updateListingBrochure],
  );

  return (
    <Stack
      data-ignore-interaction
      sx={{
        flexGrow: 1,
        height: '0',
      }}
    >
      <ListingPropertyImages
        propertyId={reportData?.property?.id}
        lotId={report.id}
        autoSave
        showCaptions={reportData?.show_image_captions_on_brochure ?? true}
        onShowCaptionsChange={handleShowCaptionsChange}
        onUpdate={() => {
          // We can't pass an empty object like in the CMA report
          // because we have a trigger on the lots table
          updateListingBrochure({
            updated_at: new Date().toISOString(),
          });
        }}
        isOnePanelDisplay
        onAdd={newImages => {
          client.cache.updateFragment(
            {
              id: `lots:${report.id}`,
              fragment: STEP_PHOTOS_BROCHURE_FRAGMENT,
              fragmentName: 'StepPhotosBrochure',
            },
            data => {
              const existingsImages = data?.property?.property_images ?? [];
              const propertyImages = [...existingsImages, ...newImages];

              return {
                ...data,
                id: data?.id ?? '',
                updated_at: data?.updated_at ?? '',
                property: {
                  ...data?.property,
                  id: data?.property?.id ?? '',
                  updated_at: data?.property?.updated_at ?? '',
                  property_images: propertyImages,
                },
              };
            },
          );
          // We can't pass an empty object like in the CMA report
          // because we have a trigger on the lots table
          updateListingBrochure({
            updated_at: new Date().toISOString(),
          });
        }}
      />
      {/* If we are not in nested route, show the footer actions */}
      {!params.imgId && (
        <FooterActionsComponentWithoutForm updating={updating} {...props} />
      )}
    </Stack>
  );
};

export default StepPhotosBrochure;
